import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { usePermissionDeniedStore } from '../stores/permissionDenied.store';
import { Breadcrumb } from '../components/Breadcrumb';

export const Route = createFileRoute('/w/$workspaceId/settings/roles')({
  component: RolesPage,
});

/** Rótulos em português para exibição no modal (backend continua em inglês). */
const PERMISSION_LABELS_PT: Record<string, string> = {
  'workspace.read': 'Ver workspace',
  'workspace.update': 'Atualizar workspace',
  'members.read': 'Ver membros',
  'members.invite': 'Convidar membros',
  'members.update_role': 'Alterar role de membros',
  'members.deactivate': 'Desativar membros',
  'roles.read': 'Ver roles',
  'roles.create': 'Criar roles',
  'roles.update': 'Atualizar roles',
  'roles.delete': 'Excluir roles',
  'roles.assign_permissions': 'Atribuir permissões a roles',
  'clients.read': 'Ver clientes',
  'clients.create': 'Criar clientes',
  'clients.update': 'Atualizar clientes',
  'clients.delete': 'Excluir clientes',
  'clients.archive': 'Arquivar clientes',
  'clients.assign_lawyer': 'Atribuir advogado ao cliente',
  'clients.contacts.read': 'Ver contatos do cliente',
  'clients.contacts.manage': 'Gerenciar contatos do cliente',
  'processes.read': 'Ver processos',
  'processes.create': 'Criar processos',
  'processes.update': 'Atualizar processos',
  'processes.delete': 'Excluir processos',
  'publications.read': 'Ver publicações',
  'publications.treat': 'Tratar publicações',
  'documents.read': 'Ver documentos',
  'documents.create': 'Criar documentos',
  'documents.delete': 'Excluir documentos',
  'tasks.read': 'Ver tarefas',
  'tasks.create': 'Criar tarefas',
  'tasks.update': 'Atualizar tarefas',
  'tasks.update_deadlines': 'Atualizar prazos da tarefa',
  'tasks.delete': 'Excluir tarefas',
  'tasks.assign': 'Atribuir tarefas',
  'tasks.change_status': 'Alterar status da tarefa',
  'agenda.view_others': 'Ver agenda de outros',
  'tags.read': 'Ver tags',
  'tags.create': 'Criar tags',
  'tags.update': 'Atualizar tags',
  'tags.delete': 'Excluir tags',
  'chat.view': 'Ver chat',
  'chat.channel.create': 'Criar canal',
  'chat.channel.update': 'Atualizar canal',
  'chat.channel.delete': 'Excluir canal',
  'chat.member.invite': 'Convidar para canal',
  'chat.member.remove': 'Remover do canal',
  'chat.message.send': 'Enviar mensagem',
  'chat.message.edit': 'Editar mensagem',
  'chat.message.delete': 'Excluir mensagem',
  'chat.message.react': 'Reagir à mensagem',
  'chat.message.attach': 'Anexar arquivo',
  'chat.message.pin': 'Fixar mensagem',
  'chat.search': 'Buscar mensagens',
  'gamification.view': 'Ver gamificação',
  'gamification.points.earn': 'Ganhar pontos',
  'gamification.experience.earn': 'Ganhar experiência',
  'gamification.level.earn': 'Subir de nível',
  'gamification.benefit.redeem': 'Resgatar benefícios',
  'gamification.benefit.create': 'Criar benefícios',
  'gamification.config.manage': 'Gerenciar configuração de pontos',
  'gamification.benefit.manage': 'Gerenciar benefícios',
  'gamification.redemption.manage': 'Gerenciar resgates',
  'metrics.view': 'Ver métricas',
  'certifications.manage': 'Gerenciar certificações',
  'certifications.view': 'Ver certificações',
  'workflow_presets.read': 'Ver lista de workflows',
  'workflow_presets.update': 'Editar workflows',
  'workflow_presets.apply': 'Usar workflows (aplicar em tarefas)',
};

const CATEGORY_LABELS_PT: Record<string, string> = {
  Workspace: 'Workspace',
  Members: 'Membros',
  Roles: 'Roles',
  Clients: 'Clientes',
  Processes: 'Processos',
  Publications: 'Publicações',
  Documents: 'Documentos',
  Tasks: 'Tarefas',
  Tags: 'Tags',
  Chat: 'Chat',
  Gamification: 'Gamificação',
  Metrics: 'Métricas',
  Certifications: 'Certificações',
  WorkflowPresets: 'Workflow Presets',
};

function getPermissionLabelPt(perm: { key?: string; name?: string }): string {
  if (perm.key && PERMISSION_LABELS_PT[perm.key]) return PERMISSION_LABELS_PT[perm.key];
  return perm.name || perm.key || '';
}

// Default permissions for system roles (from seed)
const DEFAULT_ROLE_PERMISSIONS: Record<string, string[]> = {
  ADMIN: [
    'workspace.read', 'members.read', 'members.invite', 'members.update_role', 'members.deactivate',
    'roles.read', 'roles.create', 'roles.update', 'roles.delete', 'roles.assign_permissions',
    'clients.read', 'clients.create', 'clients.update', 'clients.delete', 'clients.archive', 'clients.assign_lawyer',
    'processes.read', 'processes.create', 'processes.update', 'processes.delete',
    'documents.read', 'documents.create', 'documents.delete',
    'tasks.read', 'tasks.create', 'tasks.update', 'tasks.delete', 'tasks.assign', 'tasks.change_status', 'agenda.view_others',
    'workflow_presets.read', 'workflow_presets.update', 'workflow_presets.apply',
    'certifications.manage', 'certifications.view',
  ],
  LAWYER: [
    'members.read',
    'roles.read',
    'clients.read', 'clients.create', 'clients.update', 'clients.archive', 'clients.assign_lawyer',
    'processes.read', 'processes.create', 'processes.update', 'processes.delete',
    'documents.read', 'documents.create', 'documents.delete',
    'tasks.read', 'tasks.create', 'tasks.update', 'tasks.delete', 'tasks.assign', 'tasks.change_status', 'agenda.view_others',
    'workflow_presets.read', 'workflow_presets.update', 'workflow_presets.apply',
  ],
  PARALEGAL: [
    'members.read',
    'roles.read',
    'clients.read', 'processes.read', 'processes.update',
    'documents.read', 'documents.create',
    'tasks.read', 'tasks.create', 'tasks.update', 'tasks.change_status',
    'workflow_presets.read', 'workflow_presets.apply',
    'certifications.view',
  ],
  FINANCE: [
    'members.read',
    'roles.read',
    'clients.read', 'processes.read', 'documents.read', 'tasks.read',
    'workflow_presets.read',
  ],
  VIEWER: [
    'members.read',
    'roles.read',
    'clients.read', 'processes.read', 'documents.read', 'tasks.read',
    'workflow_presets.read',
  ],
};

function RolesPage() {
  const { workspaceId } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const { requirePermission } = usePermissionGuard();
  const showPermissionDenied = usePermissionDeniedStore((state) => state.show);
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createRoleName, setCreateRoleName] = useState('');
  const [createRoleDescription, setCreateRoleDescription] = useState('');
  const [createRolePermissions, setCreateRolePermissions] = useState<Set<string>>(new Set());
  const [permissionSearchQuery, setPermissionSearchQuery] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const canRead = hasPermission('roles.read');

  useEffect(() => {
    if (!location.pathname.startsWith('/w/')) return;
    if (!canRead) {
      showPermissionDenied('Ver roles');
      navigate({ to: '/w/$workspaceId/clients', params: { workspaceId } });
    }
  }, [location.pathname, canRead, showPermissionDenied, navigate, workspaceId]);

  const { data: roles, isLoading } = useQuery({
    queryKey: ['roles', workspaceId],
    queryFn: async () => {
      const response = await api.get('/roles');
      return response.data.data;
    },
    enabled: canRead,
  });

  const { data: allPermissions } = useQuery({
    queryKey: ['permissions'],
    queryFn: async () => {
      const response = await api.get('/permissions');
      return response.data.data;
    },
    enabled: canRead,
  });

  const permissionsByCategory = useMemo(() => {
    if (!allPermissions) return {};
    const grouped: Record<string, any[]> = {};
    allPermissions.forEach((perm: any) => {
      if (!grouped[perm.category]) {
        grouped[perm.category] = [];
      }
      grouped[perm.category].push(perm);
    });
    return grouped;
  }, [allPermissions]);

  const filteredPermissionsByCategory = useMemo(() => {
    if (!permissionSearchQuery.trim()) return permissionsByCategory;
    const q = permissionSearchQuery.trim().toLowerCase();
    const out: Record<string, any[]> = {};
    Object.entries(permissionsByCategory).forEach(([category, perms]) => {
      const categoryPt = CATEGORY_LABELS_PT[category] ?? category;
      const matching = perms.filter((perm: any) => {
        const labelPt = getPermissionLabelPt(perm);
        return labelPt.toLowerCase().includes(q) || (perm.key && perm.key.toLowerCase().includes(q)) || categoryPt.toLowerCase().includes(q);
      });
      if (matching.length > 0) out[category] = matching;
    });
    return out;
  }, [permissionsByCategory, permissionSearchQuery]);

  const visiblePermissionIds = useMemo(() => {
    const ids: string[] = [];
    Object.values(filteredPermissionsByCategory).forEach((perms: any[]) => perms.forEach((p: any) => ids.push(p.id)));
    return ids;
  }, [filteredPermissionsByCategory]);

  const updatePermissionsMutation = useMutation({
    mutationFn: async ({ roleId, permissionIds }: { roleId: string; permissionIds: string[] }) => {
      const response = await api.put(`/roles/${roleId}/permissions`, { permissionIds });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles', workspaceId] });
      setEditingRole(null);
      setSelectedPermissions(new Set());
    },
  });

  const createRoleMutation = useMutation({
    mutationFn: async (data: { name: string; description?: string; permissionIds: string[] }) => {
      // First create the role
      const roleResponse = await api.post('/roles', {
        name: data.name,
        description: data.description,
      });
      const role = roleResponse.data.data;

      // Then assign permissions
      if (data.permissionIds.length > 0) {
        await api.put(`/roles/${role.id}/permissions`, { permissionIds: data.permissionIds });
      }

      return role;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles', workspaceId] });
      setShowCreateModal(false);
      setCreateRoleName('');
      setCreateRoleDescription('');
      setCreateRolePermissions(new Set());
      setPermissionSearchQuery('');
    },
  });

  const deleteRoleMutation = useMutation({
    mutationFn: async (roleId: string) => {
      await api.delete(`/roles/${roleId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles', workspaceId] });
      setDeleteConfirmId(null);
    },
  });

  const handleEditRole = (role: any) => {
    if (!requirePermission('roles.assign_permissions', 'Atribuir permissões a roles')) return;
    setEditingRole(role.id);
    setSelectedPermissions(new Set(role.permissions?.map((p: any) => p.id) || []));
  };

  const handleSavePermissions = (roleId: string) => {
    if (!requirePermission('roles.assign_permissions', 'Atribuir permissões a roles')) return;
    updatePermissionsMutation.mutate({
      roleId,
      permissionIds: Array.from(selectedPermissions),
    });
  };

  const handleRestoreDefault = (roleName: string) => {
    if (!requirePermission('roles.assign_permissions', 'Atribuir permissões a roles')) return;
    const defaultPerms = DEFAULT_ROLE_PERMISSIONS[roleName] || [];
    if (!allPermissions) return;

    const defaultPermIds = allPermissions
      .filter((p: any) => defaultPerms.includes(p.key))
      .map((p: any) => p.id);

    const role = roles?.find((r: any) => r.name === roleName);
    if (role) {
      updatePermissionsMutation.mutate({
        roleId: role.id,
        permissionIds: defaultPermIds,
      });
    }
  };

  const handleCreateRole = () => {
    if (!requirePermission('roles.create', 'Criar roles')) return;
    if (createRoleName.trim()) {
      createRoleMutation.mutate({
        name: createRoleName.trim(),
        description: createRoleDescription.trim() || undefined,
        permissionIds: Array.from(createRolePermissions),
      });
    }
  };

  const togglePermission = (permissionId: string, isCreate: boolean = false) => {
    if (isCreate) {
      const newSet = new Set(createRolePermissions);
      if (newSet.has(permissionId)) {
        newSet.delete(permissionId);
      } else {
        newSet.add(permissionId);
      }
      setCreateRolePermissions(newSet);
    } else {
      const newSet = new Set(selectedPermissions);
      if (newSet.has(permissionId)) {
        newSet.delete(permissionId);
      } else {
        newSet.add(permissionId);
      }
      setSelectedPermissions(newSet);
    }
  };

  const handleSelectAllPermissions = () => {
    const newSet = new Set(createRolePermissions);
    visiblePermissionIds.forEach((id) => newSet.add(id));
    setCreateRolePermissions(newSet);
  };

  const { data: workspace } = useQuery({
    queryKey: ['workspaces', workspaceId],
    queryFn: async () => {
      const response = await api.get(`/workspaces/${workspaceId}`);
      return response.data.data;
    },
    enabled: canRead,
  });

  if (!canRead) return null;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-8 py-8">
          <div 
            className="bg-white p-6"
            style={{
              borderRadius: '15px',
              boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            }}
          >
            <p 
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '12px',
                color: '#718096',
              }}
            >
              Carregando...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-8 py-6">
        <Breadcrumb
          items={[
            { label: workspace?.name || 'Workspace', to: '/w/$workspaceId/clients', params: { workspaceId } },
            { label: 'Configurações', to: '/w/$workspaceId/settings/members', params: { workspaceId } },
            { label: 'Roles' },
          ]}
        />
        
        <div 
          className="bg-white p-6 mt-6"
          style={{
            borderRadius: '15px',
            boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
          }}
        >
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#2D3748',
                  marginBottom: '0.25rem',
                }}
              >
                Gestão de Permissões e Roles
              </h1>
              <p 
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  color: '#A0AEC0',
                  fontWeight: 400,
                }}
              >
                Gerencie as permissões e roles do workspace
              </p>
            </div>
            <button
              onClick={() => {
                if (!requirePermission('roles.create', 'Criar roles')) return;
                setShowCreateModal(true);
              }}
                className="rounded-lg transition-colors hover:opacity-80"
                style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#F9FAFB',
                  boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title="Nova Role"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 5V19M5 12H19"
                    stroke="#2D3748"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roles?.map((role: any) => {
              const isEditing = editingRole === role.id;
              const canShowEdit = !role.isOwner && role.name !== 'ADMIN';
              const isSystemRole = role.isSystem || role.name === 'ADMIN';

              return (
                <div 
                  key={role.id} 
                  className="p-4 rounded-lg transition-colors"
                  style={{
                    backgroundColor: '#F9FAFB',
                    boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                  }}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h2 
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#2D3748',
                      }}
                    >
                      {role.name}
                    </h2>
                    <div className="flex items-center gap-2">
                      {role.isSystem && (
                        <span 
                          className="px-2 py-1 text-xs rounded"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            backgroundColor: '#DBEAFE',
                            color: '#1E40AF',
                          }}
                        >
                          Sistema
                        </span>
                      )}
                      {role.isOwner && (
                        <span 
                          className="px-2 py-1 text-xs rounded"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            backgroundColor: '#E9D5FF',
                            color: '#6B21A8',
                          }}
                        >
                          Owner
                        </span>
                      )}
                    </div>
                  </div>
                  {role.description && (
                    <p 
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        color: '#718096',
                        marginBottom: '1rem',
                      }}
                    >
                      {role.description}
                    </p>
                  )}
                  
                  {!isEditing ? (
                    <>
                      <p 
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '12px',
                          color: '#718096',
                          marginBottom: '1rem',
                        }}
                      >
                        {role.isOwner 
                          ? 'Todas as permissões (Owner)' 
                          : `${role.permissions?.length || 0} permissões`}
                      </p>
                      <div className="flex flex-col gap-2">
                        {canShowEdit && (
                          <button
                            onClick={() => handleEditRole(role)}
                            className="px-3 py-1.5 rounded transition-colors hover:opacity-80"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              backgroundColor: '#F9FAFB',
                              color: '#2D3748',
                            }}
                          >
                            Editar
                          </button>
                        )}
                        {role.isOwner && (
                          <p 
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '10px',
                              color: '#A0AEC0',
                              fontStyle: 'italic',
                            }}
                          >
                            O Owner sempre possui todas as permissões automaticamente
                          </p>
                        )}
                        {isSystemRole && !role.isOwner && (
                          <button
                            onClick={() => handleRestoreDefault(role.name)}
                            className="px-3 py-1.5 rounded transition-colors hover:opacity-80"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              backgroundColor: '#F9FAFB',
                              color: '#2D3748',
                            }}
                          >
                            Restaurar Default
                          </button>
                        )}
                        {!role.isSystem && !role.isOwner && (
                          <button
                            onClick={() => {
                              if (!requirePermission('roles.delete', 'Excluir roles')) return;
                              setDeleteConfirmId(role.id);
                            }}
                            className="px-3 py-1.5 rounded transition-colors hover:opacity-80"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              backgroundColor: '#F9FAFB',
                              color: '#EF4444',
                            }}
                          >
                            Deletar
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="space-y-4">
                      <div 
                        className="max-h-64 overflow-y-auto border border-gray-200 rounded p-2"
                        style={{
                          backgroundColor: '#FFFFFF',
                        }}
                      >
                        {Object.entries(permissionsByCategory).map(([category, perms]) => (
                          <div key={category} className="mb-4">
                            <h4 
                              style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#2D3748',
                                marginBottom: '0.5rem',
                              }}
                            >
                              {category}
                            </h4>
                            <div className="space-y-1">
                              {perms.map((perm: any) => (
                                <label 
                                  key={perm.id} 
                                  className="flex items-center gap-2 cursor-pointer"
                                  style={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontSize: '12px',
                                    color: '#2D3748',
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedPermissions.has(perm.id)}
                                    onChange={() => togglePermission(perm.id)}
                                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                  />
                                  <span>{perm.name}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSavePermissions(role.id)}
                          disabled={updatePermissionsMutation.isPending}
                          className="flex-1 px-3 py-1.5 rounded-[10px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            backgroundColor: '#03131B',
                            color: '#FFFFFF',
                          }}
                        >
                          Salvar
                        </button>
                        <button
                          onClick={() => {
                            setEditingRole(null);
                            setSelectedPermissions(new Set());
                          }}
                          className="flex-1 px-3 py-1.5 rounded-[10px] transition-colors hover:opacity-80"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            backgroundColor: '#F9FAFB',
                            color: '#2D3748',
                          }}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Create Role Modal - portal para ficar acima da sidebar e header */}
        {showCreateModal && createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center p-4"
            style={{ zIndex: 9998 }}
            onClick={() => {
              setShowCreateModal(false);
              setCreateRoleName('');
              setCreateRoleDescription('');
              setCreateRolePermissions(new Set());
              setPermissionSearchQuery('');
            }}
          >
            <div
              className="bg-white w-full max-w-2xl max-h-[90vh] flex flex-col rounded-[15px] border border-gray-200 overflow-hidden"
              style={{
                fontFamily: "'Poppins', sans-serif",
                boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                zIndex: 9999,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 flex-shrink-0">
                <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#2D3748' }}>Nova role</h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateRoleName('');
                    setCreateRoleDescription('');
                    setCreateRolePermissions(new Set());
                    setPermissionSearchQuery('');
                  }}
                  className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  aria-label="Fechar"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                <div>
                  <label className="block mb-2" style={{ fontSize: '12px', fontWeight: 500, color: '#2D3748' }}>
                    Nome <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={createRoleName}
                    onChange={(e) => setCreateRoleName(e.target.value)}
                    placeholder="Nome da role"
                    className="w-full px-3 py-2 rounded-[8px] focus:outline-none"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#2D3748',
                      border: '1px solid #D9D9D9',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#485A6A';
                      e.target.style.boxShadow = '0 0 0 3px rgba(72, 90, 106, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#D9D9D9';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <label className="block mb-2" style={{ fontSize: '12px', fontWeight: 500, color: '#2D3748' }}>
                    Descrição
                  </label>
                  <textarea
                    value={createRoleDescription}
                    onChange={(e) => setCreateRoleDescription(e.target.value)}
                    placeholder="Descrição da role"
                    rows={3}
                    className="w-full px-3 py-2 rounded-[8px] focus:outline-none resize-none"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#2D3748',
                      border: '1px solid #D9D9D9',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#485A6A';
                      e.target.style.boxShadow = '0 0 0 3px rgba(72, 90, 106, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#D9D9D9';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <label className="block mb-2" style={{ fontSize: '12px', fontWeight: 500, color: '#2D3748' }}>
                    Permissões
                  </label>
                  <div className="mb-2 flex gap-2">
                    <input
                      type="search"
                      value={permissionSearchQuery}
                      onChange={(e) => setPermissionSearchQuery(e.target.value)}
                      placeholder="Pesquisar permissão..."
                      className="flex-1 px-3 py-2 rounded-[8px] focus:outline-none"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        color: '#2D3748',
                        border: '1px solid #D9D9D9',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#485A6A';
                        e.target.style.boxShadow = '0 0 0 3px rgba(72, 90, 106, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#D9D9D9';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleSelectAllPermissions}
                      className="px-3 py-2 rounded-[8px] transition-colors hover:opacity-90 whitespace-nowrap"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#2D3748',
                        backgroundColor: '#F9FAFB',
                        border: '1px solid #E2E8F0',
                      }}
                    >
                      Selecionar todas
                    </button>
                  </div>
                  <div
                    className="max-h-64 overflow-y-auto rounded-[8px] border p-3"
                    style={{
                      backgroundColor: '#FAFAFA',
                      borderColor: '#E2E8F0',
                      boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                    }}
                  >
                    {Object.entries(filteredPermissionsByCategory).map(([category, perms]) => (
                      <div key={category} className="mb-4 last:mb-0">
                        <h4 className="mb-2" style={{ fontSize: '12px', fontWeight: 600, color: '#2D3748' }}>
                          {CATEGORY_LABELS_PT[category] ?? category}
                        </h4>
                        <div className="space-y-1.5">
                          {perms.map((perm: any) => (
                            <label
                              key={perm.id}
                              className="flex items-center gap-2 cursor-pointer"
                              style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#2D3748' }}
                            >
                              <input
                                type="checkbox"
                                checked={createRolePermissions.has(perm.id)}
                                onChange={() => togglePermission(perm.id, true)}
                                className="rounded border-gray-300 text-[#06141B] focus:ring-[#485A6A]"
                                style={{ accentColor: '#06141B' }}
                              />
                              <span>{getPermissionLabelPt(perm)}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                    {Object.keys(filteredPermissionsByCategory).length === 0 && (
                      <p className="text-center py-4" style={{ fontSize: '12px', color: '#718096' }}>
                        Nenhuma permissão encontrada.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
                <button
                  type="button"
                  onClick={handleCreateRole}
                  disabled={createRoleMutation.isPending || !createRoleName.trim()}
                  className="flex-1 py-2 rounded-[10px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#F5F6F5',
                    backgroundColor: '#06141B',
                    boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  {createRoleMutation.isPending ? 'Criando...' : 'Criar'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateRoleName('');
                    setCreateRoleDescription('');
                    setCreateRolePermissions(new Set());
                    setPermissionSearchQuery('');
                  }}
                  className="flex-1 py-2 rounded-[10px] transition-colors hover:opacity-90"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#2D3748',
                    backgroundColor: '#F9FAFB',
                    border: '1px solid #E2E8F0',
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Delete Confirmation Modal - portal para ficar acima da sidebar e header */}
        {deleteConfirmId && createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center p-4"
            style={{ zIndex: 9998 }}
            onClick={() => setDeleteConfirmId(null)}
          >
            <div
              className="bg-white p-8 max-w-md w-full relative rounded-[15px] border border-gray-200"
              style={{
                fontFamily: "'Poppins', sans-serif",
                boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                zIndex: 9999,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#2D3748', marginBottom: '1rem' }}>
                Confirmar exclusão
              </h2>
              <p style={{ fontSize: '12px', color: '#718096', marginBottom: '1.5rem' }}>
                Tem certeza que deseja excluir esta role? Esta ação não pode ser desfeita.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (!deleteConfirmId) return;
                    if (!requirePermission('roles.delete', 'Excluir roles')) return;
                    deleteRoleMutation.mutate(deleteConfirmId);
                  }}
                  disabled={deleteRoleMutation.isPending}
                  className="flex-1 px-4 py-2 rounded-[10px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 500,
                    backgroundColor: '#DC2626',
                    color: '#FFFFFF',
                  }}
                >
                  {deleteRoleMutation.isPending ? 'Excluindo...' : 'Excluir'}
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteConfirmId(null)}
                  className="flex-1 px-4 py-2 rounded-[10px] transition-colors hover:opacity-90"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#2D3748',
                    backgroundColor: '#F9FAFB',
                    border: '1px solid #E2E8F0',
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
}
